import { focusTrap } from "../helpers.js";

class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector("details");
    this.content =
      this.mainDetailsToggle.querySelector("summary").nextElementSibling;

    this.mainDetailsToggle.addEventListener(
      "focusout",
      this.onFocusOut.bind(this)
    );
    this.mainDetailsToggle.addEventListener("toggle", this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute("open")) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute("open");
    this.mainDetailsToggle
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
  }
}

customElements.define("details-disclosure", DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector(".header-wrapper");
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    // Keep the root mega menu state in sync for keyboard-triggered open/close.
    if (this.mainDetailsToggle.classList.contains("mega-menu")) {
      if (this.mainDetailsToggle.open) {
        document.documentElement.classList.add("mega-menu-open");
      } else {
        const hasOpenMegaMenu = document.querySelector("details.mega-menu[open]");

        if (!hasOpenMegaMenu) {
          document.documentElement.classList.remove("mega-menu-open");
        }
      }
    }

    if (
      document.documentElement.style.getPropertyValue(
        "--header-bottom-position-desktop"
      ) !== ""
    )
      return;
    document.documentElement.style.setProperty(
      "--header-bottom-position-desktop",
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define("header-menu", HeaderMenu);

class StickyHeader extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = document.querySelector(".moonbase-header");
    this.headerIsAlwaysSticky =
      this.getAttribute("data-sticky-type") === "always" ||
      this.getAttribute("data-sticky-type") === "reduce-logo-size";
    this.headerBounds = {};

    this.menuTopLevelItems = this.header.querySelectorAll(
      ".header__desktop-menu-item header-menu"
    );

    this.menuTopLevelItems.forEach((item) => {
      item.addEventListener("mouseenter", this.onHover.bind(this));
      item.addEventListener("mouseleave", this.onHoverOut.bind(this));

      // Close the mega-menu when the user moves away from the "mega-menu__inner", unless they are hovering back up to the top level link
      const megaMenuInner = item.querySelector(".mega-menu__inner");
      if (megaMenuInner) {
        megaMenuInner.addEventListener("mouseleave", (event) => {
          const rect = item.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right ||
              event.clientY < rect.top || event.clientY > rect.bottom) {
                const megaMenu = item.querySelector(".mega-menu");
                if (megaMenu) {
                  megaMenu.open = false;
                  if (!document.querySelector("details.mega-menu[open]")) {
                    document.documentElement.classList.remove("mega-menu-open");
                  }
                }
          }
        });
      }
    });

    this.setHeaderHeight();

    window
      .matchMedia("(max-width: 990px)")
      .addEventListener("change", this.setHeaderHeight.bind(this));

    if (this.headerIsAlwaysSticky) {
      this.header.classList.add("shopify-section-header-sticky");
    }

    this.currentScrollTop = 0;
    this.preventReveal = false;
    this.predictiveSearch = this.querySelector("predictive-search");

    this.onScrollHandler = this.onScroll.bind(this);
    this.hideHeaderOnScrollUp = () => (this.preventReveal = true);

    this.addEventListener("preventHeaderReveal", this.hideHeaderOnScrollUp);
    window.addEventListener("scroll", this.onScrollHandler, false);

    this.createObserver();
  }

  onHover(event) {
    const e = event;
    const megaMenu = e.target.querySelector(".mega-menu");
    if (megaMenu) {
      this.hoverTimeout = setTimeout(() => {
        megaMenu.open = true;
        document.documentElement.classList.add("mega-menu-open");
      }, 100);
    }
  }

  onHoverOut(event) {
    const e = event;
    const megaMenu = e.target.querySelector(".mega-menu");
    if (megaMenu) {
      clearTimeout(this.hoverTimeout);
      megaMenu.open = false;
      if (!document.querySelector("details.mega-menu[open]")) {
        document.documentElement.classList.remove("mega-menu-open");
      }
    }
  }

  isInViewport(element) {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <=
        (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }

  setHeaderHeight() {
    const announcementBar = document.querySelector(
      ".moonbase-announcement-bar"
    );
    let headerHeight = this.header.offsetHeight;

    if (announcementBar && this.isInViewport(announcementBar)) {
      headerHeight += announcementBar.offsetHeight;
    } else {
      headerHeight = this.header.offsetHeight;
    }

    document.documentElement.style.setProperty(
      "--header-height",
      `${headerHeight}px`
    );
  }

  disconnectedCallback() {
    this.removeEventListener("preventHeaderReveal", this.hideHeaderOnScrollUp);
    window.removeEventListener("scroll", this.onScrollHandler);
  }

  createObserver() {
    let observer = new IntersectionObserver((entries, observer) => {
      this.headerBounds = entries[0].intersectionRect;
      observer.disconnect();
    });

    observer.observe(this.header);
  }

  onScroll() {
    if (document.body.classList.contains('collection')) {
        return;
        // Don't apply scrolled past class for specific pages
    }

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

    if (
      scrollTop > this.currentScrollTop &&
      scrollTop > this.headerBounds.bottom
    ) {
      this.header.classList.add("scrolled-past-header");
      if (this.preventHide) return;
      requestAnimationFrame(this.hide.bind(this));
    } else if (
      scrollTop < this.currentScrollTop &&
      scrollTop > this.headerBounds.bottom
    ) {
      this.setHeaderHeight();
      this.header.classList.add("scrolled-past-header");
      if (!this.preventReveal) {
        requestAnimationFrame(this.reveal.bind(this));
      } else {
        window.clearTimeout(this.isScrolling);

        this.isScrolling = setTimeout(() => {
          this.preventReveal = false;
        }, 66);

        requestAnimationFrame(this.hide.bind(this));
      }
    } else if (scrollTop <= this.headerBounds.top) {
      this.setHeaderHeight();
      this.header.classList.remove("scrolled-past-header");
      requestAnimationFrame(this.reset.bind(this));
    }

    this.currentScrollTop = scrollTop;
  }

  hide() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.add(
      "shopify-section-header-hidden",
      "shopify-section-header-sticky"
    );
    this.closeMenuDisclosure();
  }

  reveal() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.add("shopify-section-header-sticky", "animate");
    this.header.classList.remove("shopify-section-header-hidden");
  }

  reset() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.remove(
      "shopify-section-header-hidden",
      "shopify-section-header-sticky",
      "animate"
    );
  }

  closeMenuDisclosure() {
    this.details = this.details || this.header.querySelectorAll("details");
    this.details.forEach((detail) => detail.removeAttribute("open"));
  }
}

customElements.define("sticky-header", StickyHeader);

document.addEventListener("click", (event) => {
  const closeMegaMenuButton = event.target.closest("#js-close-megamenu");
  if (!closeMegaMenuButton) return;

  const megaMenu = closeMegaMenuButton.closest(".mega-menu");
  if (!megaMenu) return;

  const wasKeyboardActivated = event.detail === 0;
  const megaMenuSummary = megaMenu.querySelector("summary");

  megaMenu.open = false;
  if (!document.querySelector("details.mega-menu[open]")) {
    document.documentElement.classList.remove("mega-menu-open");
  }

  if (wasKeyboardActivated && megaMenuSummary) {
    megaMenuSummary.focus();
  }
});


/*** Search ***/

/* Desktop Search Overlay */
const openSearch = document.getElementById("js-open-search-overlay");
const closeSearch = document.getElementById("js-close-search-overlay");
const headerSearchOverlay = document.getElementById("js-header__search-overlay");
const predictiveSearchResults = document.querySelector('.predictive-search__results');
const searchFormDesktop = document.querySelector(".search-form--desktop");

let searchOverlayTrap;

openSearch.addEventListener("click", () => {
  headerSearchOverlay.classList.add('active');
  document.documentElement.classList.add("overlay--open", "no-overlay-after");
  headerSearchOverlay.setAttribute("aria-hidden", "false");
  headerSearchOverlay.removeAttribute("inert");

  searchOverlayTrap = focusTrap(headerSearchOverlay);
  searchOverlayTrap.activate();

  window.escManager.add(closeOverlay);

  const searchInput = document.querySelector("#js-header__search-overlay .js-search-input");
  if (searchInput) {
    searchInput.focus();
  }
});
/* Shared close function */
function closeOverlay() {
  document.documentElement.classList.remove("overlay--open", "no-overlay-after");
  headerSearchOverlay.classList.remove("active");

  headerSearchOverlay.setAttribute("aria-hidden", "true");
  headerSearchOverlay.setAttribute("inert", "");

  document.documentElement.classList.remove("predictive-search-open");

  document.querySelector('.header__search-overlay .js-search-input').value = '';
  predictiveSearchResults.classList.remove('block');
  predictiveSearchResults.classList.add('hidden');

  searchOverlayTrap?.deactivate();
  window.escManager.remove(closeOverlay);
}

// Close the search via button that can only be tabbed to
closeSearch.addEventListener("click", closeOverlay);

// Close the search overlay when the user moves away from the "search-form" on desktop only
if (searchFormDesktop) {
  searchFormDesktop.addEventListener("mouseleave", closeOverlay);
}

/* Mobile Search Overlay (within mobile nav drawer) */
const mobileSearchField = document.querySelector('.side-drawer__search .js-search-input');
const mobileSearchPopular = document.querySelector('.side-drawer__search .popular-searches');
const mobileSearchRecent = document.querySelector('.side-drawer__search .recent-searches');
const mobileDrawerNav = document.querySelector('.side-drawer__navigation');

mobileSearchField.addEventListener("click", () => {
  mobileSearchPopular.classList.replace('hidden', 'block');
  mobileSearchRecent.classList.replace('hidden', 'block');
  mobileDrawerNav.classList.add('hidden');
});

/* Reset search when closing mobile drawer */
const closeMobileDrawer = document.querySelector('.js-mobile-nav-drawer summary');
const mobilePredictiveSearchResults = document.querySelector('.js-mobile-nav-drawer .predictive-search__results');

closeMobileDrawer.addEventListener("click", () => {
  mobileSearchPopular.classList.replace('block', 'hidden');
  mobileSearchRecent.classList.replace('block', 'hidden');
  mobileDrawerNav.classList.remove('hidden');
  predictiveSearchResults.classList.remove('block');
  predictiveSearchResults.classList.add('hidden');
  document.documentElement.classList.remove("predictive-search-open");
  document.querySelector('.side-drawer__search .js-search-input').value = '';
});


/* Mega Menu - show collection image on hover */
function megaMenuCollectionImageHover() {
  document.addEventListener("DOMContentLoaded", function () {
    const links = document.querySelectorAll(".mega-menu__link");
    const imageContainer = document.querySelector("[data-collection-image-container]");
    if (!imageContainer) return; // Ensure the target exists
    const originalContent = imageContainer.innerHTML;

    links.forEach(link => {
      if (link.hasAttribute("data-collection-image")) {
        link.addEventListener("mouseover", function () {
          imageContainer.classList.add('fade-out'); // Start fade-out effect
          imageContainer.classList.remove('opacity-0');
          setTimeout(() => {
            imageContainer.innerHTML = `<img class="w-full h-full object-cover fade" src="${link.getAttribute('data-collection-image')}">`;
            imageContainer.classList.remove('fade-out'); // Remove fade-out
            imageContainer.classList.add('fade-in'); // Apply fade-in effect
          }, 200);
        });

        link.addEventListener("mouseout", function () {
          imageContainer.classList.add('fade-out'); // Start fade-out effect
          imageContainer.classList.add('opacity-0');
          setTimeout(() => {
            imageContainer.innerHTML = originalContent;
            imageContainer.classList.remove('fade-in'); // Reset fade-in
            imageContainer.classList.remove('fade-out'); // Reset fade-out
          }, 200); // Matches transition duration
        });
      }
    });
  });
}

megaMenuCollectionImageHover();


/* Pause all page videos when mega menu or search is open */

function onClassAdded(className) {
  const videos = document.getElementsByTagName("video");
  Array.from(videos).forEach(video => video.pause());
}

function onClassRemoved(className) {
  const videos = document.getElementsByTagName("video");
  Array.from(videos).forEach(video => video.play());
}

const pauseVideoClasses = ['mega-menu-open', 'overlay--open'];
let previousClassList = new Set(document.documentElement.classList);

const observer = new MutationObserver((mutationsList) => {
  for (const mutation of mutationsList) {
    if (mutation.attributeName === 'class') {
      const currentClasses = new Set(mutation.target.classList);
      for (const cls of pauseVideoClasses) {
        const hadClass = previousClassList.has(cls);
        const hasClass = currentClasses.has(cls);
        if (!hadClass && hasClass) {
          onClassAdded(cls);
        } else if (hadClass && !hasClass) {
          onClassRemoved(cls);
        }
      }
      previousClassList = currentClasses;
    }
  }
});

observer.observe(document.documentElement, {
  attributes: true,
  attributeFilter: ['class'],
});
